/* eslint-disable complexity */
import { put, takeEvery, all, call, select } from 'redux-saga/effects';
import { setAction as setChartAction } from '../actions/chartEditor';
import actionTypes from '../../../redux/actions/action-types';
import { merge, parseCSV } from 'highed';
import { updateAggregated, initChart } from './ChartEditor';
import { getProjectConfig } from '../../../redux/selectors/projectConfig';
import { setAction as setProjectConfigAction } from '../../../redux/actions/projectConfig';
import templates from '../../../editor/core/highed.template.plugins';
import { isTilemap } from './ChartEditorMapHelper';
import { setData, setTemplateData } from './ChartEditorData';
import { getChartConfig } from '../selectors/chartEditor';
import allTemplates from './../../../editor/meta/highed.meta.templates';
import { cloneDeep } from 'lodash';

const defaultMap = 'countries/us/us-all';

function getTemplateType(aggregatedOptions) {
  if (aggregatedOptions?.data) {
    if (aggregatedOptions.data.csv) return 'csv';
    if (aggregatedOptions.data.googleSpreadsheetKey) return 'google';
    if (aggregatedOptions.data.text) return 'text';
    if (aggregatedOptions.data.csvURL || aggregatedOptions.data.rowsURL || aggregatedOptions.data.columnsURL) {
      return 'live';
    }
  }

  return 'csv';
}

function getDataOptions(aggregatedOptions, dataOptions, dataType) {
  if (dataType === 'csv') {
    return cloneDeep(dataOptions);
  } else if (dataType === 'google' || dataType === 'live') {
    return cloneDeep(aggregatedOptions?.data ?? {});
  }

  return cloneDeep(aggregatedOptions?.data ?? {});
}

function getDataProperties(aggregatedOptions, dataOptions) {
  const dataType = getTemplateType(aggregatedOptions);
  return {
    dataType,
    dataOptions: getDataOptions(aggregatedOptions, dataOptions, dataType)
  };
}

export function* setTemplate(params) {
  try {
    let { template, useUserData } = params.data;

    if (!template) return;

    const config = yield select(getProjectConfig);
    const chartConfig = yield select(getChartConfig);
    let customizedOptions = merge({}, config.customizedOptions);

    let chosenWizardTemplate = false;
    if (template.sampleSet) chosenWizardTemplate = template;

    const isNowTilemap = isTilemap(chosenWizardTemplate);
    const wasTilemap = isTilemap(chartConfig.chosenWizardTemplate);

    let dropdownValues = {};
    // Sort out dropdown values
    if (isNowTilemap && !wasTilemap) {
      // Is now a tilemap coming from a normal map
      dropdownValues = {
        countryValue: {
          label: 'United States of America',
          value: 'honeycomb-us'
        }
      };
      delete customizedOptions.chart.map;
    } else if (!isNowTilemap && wasTilemap) {
      // Is now a normal map coming from a tilemap
      dropdownValues = {
        countryValue: {
          label: 'United States of America',
          value: `${defaultMap}.js`
        },
        categoryValue: {
          label: 'Standard',
          value: `${defaultMap}.js`
        }
      };
      if (!customizedOptions.chart) customizedOptions.chart = {};
      customizedOptions.chart.map = defaultMap;
    }

    if (customizedOptions.yAxis) {
      delete customizedOptions.yAxis;
    }
    delete customizedOptions.colorAxis;

    const templateMeta = [
      {
        templateTitle: template.title,
        templateHeader: template.advancedParent,
        templateParseData: template.parseData
      }
    ];

    const projectConfigProps = {};

    if (!useUserData) {
      let dataOptions = yield call(setTemplateData, customizedOptions, chosenWizardTemplate, isNowTilemap);

      let { defaultOptions } = dataOptions;
      customizedOptions = dataOptions.customizedOptions;

      if (defaultOptions.customizedOptions) {
        customizedOptions = merge(customizedOptions, merge({}, defaultOptions.customizedOptions));
      }
      projectConfigProps.dataOptions = parseCSV(defaultOptions.data);
    } else {
      (customizedOptions.series ?? []).forEach((series) => (series.type = template?.config?.chart?.type ?? 'line'));
    }

    let plugins = {};
    let pluginConfig = {};

    if (chosenWizardTemplate.plugins) {
      chosenWizardTemplate.plugins.forEach((p) => {
        plugins[p] = true;
        if (templates[p]?.config) {
          pluginConfig = templates[p].config;
        }
      });
    }

    if (chosenWizardTemplate.isMap) plugins.map = 1;

    if (template.config?.chart?.type) {
      if (!customizedOptions.chart) customizedOptions.chart = {};
      customizedOptions.chart.type = template.config.chart.type;
    } else delete customizedOptions.chart.type;

    yield put(
      setChartAction({
        chosenWizardTemplate,
        constr: chosenWizardTemplate.constructor || 'Chart',
        ...dropdownValues
      })
    );

    yield put(
      setProjectConfigAction({
        customizedOptions,
        templateOptions: [merge({}, template.config || {})],
        // assignDataFields,
        plugins,
        templateMeta,
        changeMade: true,
        ...projectConfigProps,
        pluginConfig,
        useDefaultData: !useUserData
      })
    );

    yield call(updateAggregated);

    if (useUserData) {
      const { dataType, dataOptions } = getDataProperties(config.aggregatedOptions, config.dataOptions);
      yield call(setData, {
        data: {
          options: dataOptions,
          dataType,
          skipNotification: true
        }
      });
    } else {
      yield call(initChart);
    }
  } catch (e) {
    throw new Error(e);
  }
}

export function getTemplateData(params) {
  const { template } = params.data;

  if (template?.[0]?.templateHeader && template[0].templateTitle) {
    const header = template[0].templateHeader;
    const title = template[0].templateTitle;
    return allTemplates[header]?.templates?.[title] ?? false;
  }

  return false;
}

/** Watch functions */
export function* watchSetTemplate() {
  yield takeEvery(actionTypes.chartEditor.setTemplate, setTemplate);
}

export default function* rootSaga() {
  yield all([watchSetTemplate()]);
}
